import { useCallback, useEffect, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import * as api from "../api/client";

// One row per real column of `tableName`, each with its own inline save — matches RolesPage's
// toggle-saves-immediately pattern rather than one big form, since a description change is a small,
// independent edit with nothing to coordinate across rows.
function ColumnRow({ tableName, column, initialDescription, onSaved }) {
  const [value, setValue] = useState(initialDescription || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const dirty = value !== (initialDescription || "");

  async function save() {
    setSaving(true);
    setError("");
    try {
      await api.upsertColumnDescription(tableName, column.name, value);
      onSaved(column.name, value.trim());
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="col-desc-row">
      <div className="col-desc-name">
        <div>{column.name}</div>
        <div className="col-desc-type">{column.type}</div>
      </div>
      <div className="col-desc-input">
        <textarea
          rows={2}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="No description yet — add one so the agent knows what this column means"
        />
        {error && <div className="col-desc-error">{error}</div>}
      </div>
      <div className="col-desc-save">
        <button type="button" className="dt-icon-btn" disabled={!dirty || saving} title="Save" onClick={save}>
          {saving ? <Spinner animation="border" size="sm" /> : "✓"}
        </button>
      </div>
    </div>
  );
}

export default function ColumnDescriptionsModal({ tableName, onClose }) {
  const [columns, setColumns] = useState([]);
  const [descriptions, setDescriptions] = useState({}); // { columnName: description }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [c, d] = await Promise.all([
        api.listDbColumns(tableName),
        api.listColumnDescriptions(tableName),
      ]);
      setColumns(c.columns || []);
      const map = {};
      for (const row of d.descriptions || []) map[row.columnName] = row.description;
      setDescriptions(map);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [tableName]);

  useEffect(() => {
    load();
  }, [load]);

  function handleSaved(columnName, description) {
    setDescriptions((prev) => {
      const next = { ...prev };
      if (description) next[columnName] = description;
      else delete next[columnName];
      return next;
    });
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="form-modal" style={{ width: "min(700px, 100%)" }} onClick={(e) => e.stopPropagation()}>
        <div className="form-modal-head">
          <div className="form-modal-title">Columns in "{tableName}"</div>
          <button className="icon-btn" onClick={onClose} title="Close">
            ✕
          </button>
        </div>
        <div className="form-modal-body" style={{ maxHeight: "60vh" }}>
          {error && <Alert variant="danger">{error}</Alert>}
          {loading ? (
            <Spinner animation="border" />
          ) : columns.length === 0 ? (
            <div style={{ color: "var(--muted)" }}>This table has no columns (or no longer exists).</div>
          ) : (
            <div className="col-desc-list">
              {columns.map((c) => (
                <ColumnRow
                  key={c.name}
                  tableName={tableName}
                  column={c}
                  initialDescription={descriptions[c.name]}
                  onSaved={handleSaved}
                />
              ))}
            </div>
          )}
        </div>
        <div className="form-modal-foot">
          <button type="button" className="dash-btn" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
